import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * LOADING SERVICE
 * Counter-based tracking of in-flight HTTP requests.
 *
 * Design:
 *  - Counter (not boolean) — so N parallel calls = 1 spinner, no flicker
 *  - 300ms delay before showing — fast calls (<300ms) never flash the spinner
 *  - Auto-hides when counter returns to 0
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
  private activeCount = 0;
  private _visible$ = new BehaviorSubject<boolean>(false);
  visible$ = this._visible$.asObservable();

  private readonly SHOW_DELAY_MS = 300;
  private showTimer: any = null;

  /** Saves still on their way to the server (TC-4529: "Update Now" must not reload through one). */
  writesInFlight = 0;

  /** Call when an HTTP request begins */
  start(): void {
    this.activeCount++;
    if (this.activeCount === 1 && !this.showTimer) {
      this.showTimer = setTimeout(() => {
        if (this.activeCount > 0) this._visible$.next(true);
        this.showTimer = null;
      }, this.SHOW_DELAY_MS);
    }
  }

  /** Call when an HTTP request completes (success or failure) */
  stop(): void {
    this.activeCount = Math.max(0, this.activeCount - 1);
    if (this.activeCount === 0) {
      if (this.showTimer) {
        clearTimeout(this.showTimer);
        this.showTimer = null;
      }
      this._visible$.next(false);
    }
  }

  /** Force-reset — use only on route changes / logout to clear stuck spinners */
  reset(): void {
    this.activeCount = 0;
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    this._visible$.next(false);
  }

  get isVisible(): boolean {
    return this._visible$.value;
  }
}
